import { Navigate, Route, Routes, useParams } from 'react-router-dom';
import { lazy } from 'react';
import { StandardLayout } from '@/shared/layouts/StandardLayout';
import SuspenseComponent from '@/shared/components/Generic/SuspenseComponent/SuspenseComponent';

const HomePage = lazy(() => import('@/modules/publicPortal/homePage/HomePage'));
const ArticlesPage = lazy(() => import('@/modules/publicPortal/articlesPage/ArticlesPage'));
const CategoriesPage = lazy(() => import('@/modules/publicPortal/categoriesPage/CategoriesPage'));
const CategoryDetailsPage = lazy(
  () => import('@/modules/publicPortal/categoryDetailsPage/categoryDetailsPage')
);
const ArticleDetailsPage = lazy(
  () => import('@/modules/publicPortal/articleDetailsPage/ArticleDetailsPage')
);
const EventsPage = lazy(() => import('@/modules/publicPortal/eventsPage/EventsPage'));
const EventDetailsPage = lazy(
  () => import('@/modules/publicPortal/eventDetailsPage/EventDetailsPage')
);
const BrandsPage = lazy(() => import('@/modules/publicPortal/brandsPage/BrandsPage'));
const BrandDetailsPage = lazy(
  () => import('@/modules/publicPortal/brandDetailsPage/BrandDetailsPage')
);
const BrandComparePage = lazy(
  () => import('@/modules/publicPortal/brandComparePage/BrandComparePage')
);

const SuppliersPage = lazy(() => import('@/modules/publicPortal/SuppliersPage/SuppliersPage'));
const IngredientsPage = lazy(
  () => import('@/modules/publicPortal/ingredientsPage/IngredientsPage')
);
const IngredientDetailsPage = lazy(
  () => import('@/modules/publicPortal/ingredientDetailsPage/IngredientDetailsPage')
);
const SupplierDetailsPage = lazy(
  () => import('@/modules/publicPortal/SuppliersPage/SupplierDetailsPage')
);
const AboutPage = lazy(() => import('@/modules/publicPortal/aboutPage/AboutPage'));
const PriceListPage = lazy(() => import('@/modules/publicPortal/priceListPage/PriceListPage'));
const SearchResultsPage = lazy(() => import('@/modules/publicPortal/searchPage/SearchResultsPage'));
const B2BRegistrationRedirect = lazy(
  () => import('@/shared/components/B2BRegistrationRedirect/B2BRegistrationRedirect')
);
const B2BLoginRedirect = lazy(
  () => import('@/shared/components/B2BLoginRedirect/B2BLoginRedirect')
);
const ContactPage = lazy(() => import('@/modules/publicPortal/contactPage/ContactPage'));
const TermsConditionsPage = lazy(
  () => import('@/modules/publicPortal/termsConditionsPage/TermsConditionsPage')
);
const PrivacyPolicyPage = lazy(
  () => import('@/modules/publicPortal/privacyPolicyPage/PrivacyPolicyPage')
);
const AffiliateDisclosurePage = lazy(
  () => import('@/modules/publicPortal/affiliateDisclosurePage/AffiliateDisclosurePage')
);
const ExternalRedirect = lazy(
  () => import('@/shared/components/ExternalRedirect/ExternalRedirect')
);
const NotFoundPage = lazy(() => import('@/modules/publicPortal/notFoundPage/NotFoundPage'));
const AccountActivatePage = lazy(
  () => import('@/modules/publicPortal/accountActivatePage/AccountActivatePage')
);

function ArticleRedirect() {
  const { slug } = useParams();
  return <Navigate to={`/news/${slug}`} replace />;
}

function CategoryRedirect() {
  const { slug } = useParams();
  return <Navigate to={`/health-categories/${slug}`} replace />;
}

function SupplementFactRedirect() {
  const { slug } = useParams();
  return <Navigate to={`/ingredients/${slug}`} replace />;
}

const PublicPortalRoutes = () => {
  return (
    <SuspenseComponent>
      <StandardLayout>
        <Routes>
          <Route path='/' element={<HomePage />} />
          <Route path='/news' element={<ArticlesPage />} />
          <Route path='/news/:articleSlug' element={<ArticleDetailsPage />} />
          <Route path='/health-categories' element={<CategoriesPage />} />
          <Route path='/health-categories/:categorySlug' element={<CategoryDetailsPage />} />
          <Route path='/events' element={<EventsPage />} />
          <Route path='/events/:eventSlug' element={<EventDetailsPage />} />
          <Route path='/brands' element={<BrandsPage />} />
          <Route path='/brands/compare' element={<BrandComparePage />} />
          <Route path='/brands/:brandSlug' element={<BrandDetailsPage />} />
          <Route path='/suppliers' element={<SuppliersPage />} />
          <Route path='/ingredients' element={<IngredientsPage />} />
          <Route path='/ingredients/:ingredientSlug' element={<IngredientDetailsPage />} />
          <Route path='/suppliers/:supplierSlug' element={<SupplierDetailsPage />} />
          <Route path='/about-us' element={<AboutPage />} />
          <Route path='/price-list' element={<PriceListPage />} />
          <Route path='/search' element={<SearchResultsPage />} />
          <Route path='/register' element={<B2BRegistrationRedirect />} />
          <Route path='/login' element={<B2BLoginRedirect />} />
          <Route path='/contact' element={<ContactPage />} />
          <Route path='/terms-conditions' element={<TermsConditionsPage />} />
          <Route path='/privacy-policy' element={<PrivacyPolicyPage />} />
          <Route path='/affiliate-disclosure' element={<AffiliateDisclosurePage />} />

          {/* Legacy redirects — preserve SEO for old WordPress URLs */}
          <Route path='/articles' element={<Navigate to='/news' replace />} />
          <Route path='/articles/:slug' element={<ArticleRedirect />} />
          <Route path='/categories' element={<Navigate to='/health-categories' replace />} />
          <Route path='/categories/:slug' element={<CategoryRedirect />} />
          <Route path='/supplement-fact/:slug' element={<SupplementFactRedirect />} />
          <Route path='/supplement-facts' element={<Navigate to='/ingredients' replace />} />
          <Route path='/blog' element={<Navigate to='/news' replace />} />
          <Route path='/ingredients_list' element={<Navigate to='/ingredients' replace />} />
          <Route path='/login-register' element={<Navigate to='/login' replace />} />
          <Route path='/register-brand' element={<Navigate to='/register' replace />} />
          <Route path='/about' element={<Navigate to='/about-us' replace />} />
          <Route path='/auth/login' element={<Navigate to='/login' replace />} />
          <Route path='/auth/activate/:token' element={<AccountActivatePage />} />
          <Route path='/en/auth/activate/:token' element={<AccountActivatePage />} />
          <Route path='/members/*' element={<ExternalRedirect />} />
          <Route path='/account/*' element={<ExternalRedirect />} />

          {/* 404 catch-all */}
          <Route path='*' element={<NotFoundPage />} />
        </Routes>
      </StandardLayout>
    </SuspenseComponent>
  );
};

export default PublicPortalRoutes;
